package dev.terrakok.mapitan.domain

fun Document.apply(operation: Operation): Document = when (operation) {
    is Operation.AddPin -> copy(pins = pins + operation.pin)
    is Operation.RemovePin -> copy(pins = pins - operation.pin)
    is Operation.ColorPin -> {
        val old = findById(operation.pinId) as? Pin ?: return this
        return this.copy(pins = pins - old + old.copy(color = operation.newColor))
    }

    is Operation.MovePin -> {
        val old = findById(operation.pinId) as? Pin ?: return this
        return this.copy(pins = pins - old + old.copy(point = operation.newPoint))
    }

    is Operation.AddLabel -> copy(labels = labels + operation.label)
    is Operation.RemoveLabel -> copy(labels = labels.filterNot { it.id == operation.label.id })
    is Operation.ColorLabel -> updateLabel(operation.labelId) { label ->
        label.copy(color = operation.newColor)
    }

    is Operation.NewLabelText -> updateLabel(operation.labelId) { label ->
        label.copy(text = operation.newText)
    }

    is Operation.MoveLabel -> updateLabel(operation.labelId) { label ->
        label.copy(point = operation.newPoint)
    }

    is Operation.AddTrack -> copy(tracks = tracks + operation.track)
    is Operation.RemoveTrack -> copy(tracks = tracks.filterNot { it.id == operation.track.id })
    is Operation.ColorTrack -> updateTrack(operation.trackId) { track ->
        track.copy(color = operation.newColor)
    }

    is Operation.NewTrackLineThickness -> updateTrack(operation.trackId) { track ->
        track.copy(lineThickness = operation.newThickness)
    }

    is Operation.NewTrackLineStyle -> updateTrack(operation.trackId) { track ->
        track.copy(lineStyle = operation.newStyle)
    }

    is Operation.AddTrackPoint -> updateTrack(operation.trackId) { track ->
        val index = operation.index.coerceIn(0, track.points.size)
        track.copy(points = track.points.subList(0, index) + operation.point + track.points.subList(index, track.points.size))
    }

    is Operation.RemoveTrackPoint -> updateTrack(operation.trackId) { track ->
        track.copy(points = track.points.filterNot { it.id == operation.point.id })
    }

    is Operation.MoveTrackPoint -> updateTrack(operation.trackId) { track ->
        track.copy(
            points = track.points.map { p ->
                if (p.id == operation.pointId) p.copy(point = operation.newPoint) else p
            }
        )
    }

    is Operation.AddArea -> copy(areas = areas + operation.area)
    is Operation.RemoveArea -> copy(areas = areas.filterNot { it.id == operation.area.id })
    is Operation.ColorArea -> updateArea(operation.areaId) { area ->
        area.copy(color = operation.newColor)
    }

    is Operation.NewAreaLineThickness -> updateArea(operation.areaId) { area ->
        area.copy(lineThickness = operation.newThickness)
    }

    is Operation.NewAreaLineStyle -> updateArea(operation.areaId) { area ->
        area.copy(lineStyle = operation.newStyle)
    }

    is Operation.AddAreaPoint -> updateArea(operation.areaId) { area ->
        val index = operation.index.coerceIn(0, area.points.size)
        area.copy(points = area.points.subList(0, index) + operation.point + area.points.subList(index, area.points.size))
    }

    is Operation.RemoveAreaPoint -> updateArea(operation.areaId) { area ->
        area.copy(points = area.points.filterNot { it.id == operation.point.id })
    }

    is Operation.MoveAreaPoint -> updateArea(operation.areaId) { area ->
        area.copy(
            points = area.points.map { point ->
                if (point.id == operation.pointId) point.copy(point = operation.newPoint) else point
            }
        )
    }
}

private fun Document.updateTrack(trackId: String, update: (Track) -> Track): Document {
    if (tracks.none { it.id == trackId }) return this
    return copy(tracks = tracks.map { if (it.id == trackId) update(it) else it })
}

private fun Document.updateArea(areaId: String, update: (Area) -> Area): Document {
    if (areas.none { it.id == areaId }) return this
    return copy(areas = areas.map { if (it.id == areaId) update(it) else it })
}

private fun Document.updateLabel(labelId: String, update: (Label) -> Label): Document {
    if (labels.none { it.id == labelId }) return this
    return copy(labels = labels.map { if (it.id == labelId) update(it) else it })
}